import { createContext, use, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { configureHttp } from '@/api/http';
import { login as apiLogin, logout as apiLogout } from './auth-api';
import { hasPermissions, isSuperAdmin, parsePermissionMask, type Permission } from './permissions';
import { clearSession, readSession, SESSION_STORAGE_KEY, writeSession, type Session } from './session';

export const EXPIRY_WARNING_MS = 10 * 60 * 1000;

export type SignOutReason = 'user' | 'expired';

interface AuthContextValue {
  session: Session | null;
  mask: bigint;
  superAdmin: boolean;
  /** Why the last session ended, so the login page can explain it. */
  lastSignOut: SignOutReason | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  can: (...perms: Permission[]) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// Wired at import, not in an effect: route loaders start in the router's own effects, which run
// before this provider's, and a first request without the token would 401 and end the session on
// every reload or deep link. The getter reads storage only, so it needs no React state.
const readToken = () => readSession()?.token ?? null;
configureHttp({ getToken: readToken });

export function AuthProvider({ children, onSignedOut }: { children: ReactNode; onSignedOut?: () => void }) {
  const [session, setSession] = useState<Session | null>(() => readSession());
  const [lastSignOut, setLastSignOut] = useState<SignOutReason | null>(null);

  const end = useCallback(
    (reason: SignOutReason) => {
      clearSession();
      setSession(null);
      setLastSignOut(reason);
      onSignedOut?.();
    },
    [onSignedOut],
  );

  // Every request reads the stored token, so a sign-in in another tab is picked up at once.
  useEffect(() => {
    configureHttp({ getToken: readToken, onUnauthorized: () => end('expired') });
  }, [end]);

  // Sign-out or sign-in in another tab.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === SESSION_STORAGE_KEY) setSession(readSession());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // 12 h tokens with no refresh: warn ahead, then end the session at `exp`.
  useEffect(() => {
    if (!session?.expiresAt) return;
    const remaining = session.expiresAt - Date.now();
    const timers = [window.setTimeout(() => end('expired'), Math.max(0, remaining))];
    if (remaining > EXPIRY_WARNING_MS) {
      timers.push(
        window.setTimeout(
          () => toast.warning('Your session ends in 10 minutes. Save your work, then sign in again.'),
          remaining - EXPIRY_WARNING_MS,
        ),
      );
    }
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [session, end]);

  const login = useCallback(async (email: string, password: string) => {
    const next = await apiLogin(email, password);
    writeSession(next);
    setSession(next);
    setLastSignOut(null);
  }, []);

  const logout = useCallback(async () => {
    await apiLogout();
    end('user');
  }, [end]);

  const value = useMemo<AuthContextValue>(() => {
    const mask = session ? parsePermissionMask(session.user.permission) : 0n;
    return {
      session,
      mask,
      superAdmin: isSuperAdmin(mask),
      lastSignOut,
      login,
      logout,
      can: (...perms) => session !== null && hasPermissions(mask, perms),
    };
  }, [session, lastSignOut, login, logout]);

  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthContextValue {
  const ctx = use(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

/** Renders children only when the user holds every listed permission. UI gating only; the server enforces. */
export function Can({
  all,
  children,
  fallback = null,
}: {
  all: Permission[];
  children: ReactNode;
  fallback?: ReactNode;
}) {
  return useAuth().can(...all) ? children : fallback;
}
